import './styles.scss';

import { Button, Card, Form } from 'react-bootstrap';
import { Dayts, createNotification, onEnter } from '@/utils';
import React, { useRef } from 'react';
import { convertDtrLocationToPotentialLocation, getHistory, normalizeDtrLocation } from '@/features/DriveTimeTool/api/services';

import CardHeader from '@/features/DriveTimeTool/components/CardHeader';
import DateInput from '@/components/DateInput';
import DriveTimeTooltipContent from './DriveTimeTooltipContent';
import LocationInput from '@/features/DriveTimeTool/components/LocationInput';
import { Toast } from '@/models';
import { createComponentState } from '@/state';
import createQueryHistoryBulk from '../../api/services/createQueryHistoryBulk';
import { formatFullAddress } from '../../utils/locations';
import { useDriveTimeTool } from '@/features/DriveTimeTool';
import { useReportHistoryFilters } from '../History/HistoryFilters';
import { useRouteMap } from '../RouteMap';

type ControlsState = {
  startDate: string;
  endDate: string;
  puAddress: string;
  doAddress: string;
  interval: string;
};

const initControlsState: ControlsState = {
  startDate: '',
  endDate: '',
  puAddress: '',
  doAddress: '',
  interval: null,
};

const useControlsState = createComponentState<ControlsState>(initControlsState);

const Controls = (): JSX.Element => {
  const [loading, setState] = useDriveTimeTool(({ state, setState }) => [state.loading.history, setState]);
  const [form, setForm] = useControlsState(({ state, setState }) => [state, setState]);

  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);
  const pickupInputRef = useRef<HTMLInputElement>(null);
  const dropoffInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (field: keyof ControlsState, value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const handleRouteInputChange = (field: 'puAddress' | 'doAddress') => {
    const value = useControlsState.getState().state[field];
    useRouteMap.getState().setState((current) => ({
      ...current,
      [field]: value,
    }));
  };

  // START TEMP LOGIC
  // This is temporary logic to handle the fact that the API expects location IDs, but the user is inputting addresses.
  // In the future, the existing `createQueryHistoryBulk` endpoint should be updated to accept addresses and handle the location creation internally.
  const getLocation = async (address: string): Promise<string> => {
    const location = await normalizeDtrLocation(address);
    setState((current) => {
      // check if location already exists
      if (current.locations.has(location.id)) return current;
      // add new location
      current.locations.set(location.id, location);
      const key = formatFullAddress(convertDtrLocationToPotentialLocation(location));
      if (!current.groupedLocationIdsByAddress.has(key)) current.groupedLocationIdsByAddress.set(key, []);
      current.groupedLocationIdsByAddress.get(key).push(location.id);
      // update location mapping
      return {
        ...current,
        locations: current.locations,
        groupedLocationIdsByAddress: current.groupedLocationIdsByAddress,
      };
    });
    return location.id;
  };
  // END TEMP LOGIC

  const handleSubmit = async (event: React.FormEvent) => {
    try {
      event.preventDefault();
      setState((current) => ({ ...current, loading: { ...current.loading, history: true } }));

      // this is checking the form input to ensure the address text is not the same
      if (form.puAddress === form.doAddress) {
        return createNotification(
          'Pick up/ drop off location cannot be the same. Please try again.',
          Toast.Type.WARNING,
          'Drive Time Report Error'
        );
      }
      // get location ids
      const puLocationId = await getLocation(form.puAddress);
      const doLocationId = await getLocation(form.doAddress);

      // sanity check; this is possible if the getDtrNormalizedAddress doesn't return the street.
      if (puLocationId === doLocationId) console.error('pu and do location ids are the same');

      // create query history
      await createQueryHistoryBulk([
        {
          startDate: form.startDate,
          endDate: form.endDate,
          interval: form.interval,
          puLocationId,
          doLocationId,
        },
      ]);
      const filters = useReportHistoryFilters.getState().state;
      // get grouped location ids by address to search
      const state = useDriveTimeTool.getState().state;
      const puLocationIds = state.groupedLocationIdsByAddress.get(
        formatFullAddress(convertDtrLocationToPotentialLocation(state.locations.get(filters.puLocationId)))
      );
      const doLocationIds = state.groupedLocationIdsByAddress.get(
        formatFullAddress(convertDtrLocationToPotentialLocation(state.locations.get(filters.doLocationId)))
      );
      const historyRes = await getHistory({
        ...filters,
        puLocationId: puLocationIds,
        doLocationId: doLocationIds,
      });
      setState((current) => ({
        ...current,
        history: historyRes || [],
      }));
      // clear form state
      setForm({
        startDate: '',
        endDate: '',
        puAddress: '',
        doAddress: '',
        interval: null,
      });
      pickupInputRef.current.value = '';
      dropoffInputRef.current.value = '';
      createNotification(`We're generating your drive time report...`, Toast.Type.WARNING, 'Processing Drive Time Report...');
      document.querySelector('.DriveTimeTool-History').scrollIntoView({ behavior: 'smooth' });
    } catch (err) {
      console.error('Error submitting/fetching query history:', err);
      createNotification(`We couldn't generate your drive time report. Please try again.`, Toast.Type.DANGER, 'Drive Time Report Error');
    } finally {
      setState((current) => ({ ...current, loading: { ...current.loading, history: false } }));
    }
  };

  const isValidDateRange = () => {
    if (!form.startDate || !form.endDate) return false;
    const start = new Dayts(form.startDate).startOf('day');
    const end = new Dayts(form.endDate).endOf('day');
    const diffDays = end.diff(start, 'days');
    if (
      new Dayts(form.startDate).startOf('day').isSameOrBefore(new Dayts().startOf('day')) ||
      new Dayts(form.endDate).endOf('day').isSameOrBefore(new Dayts().startOf('day'))
    )
      return false;
    return diffDays >= 0 && diffDays <= 35;
  };

  const isFormValid = () => {
    return form.startDate && form.endDate && form.puAddress && form.doAddress && form.puAddress !== form.doAddress && isValidDateRange();
  };

  return (
    <div className="Controls">
      <Card className="query-builder-card">
        <CardHeader icon="fas fa-search" tooltip={<DriveTimeTooltipContent />} subtitle="Create a report using our predictive model.">
          Drive Time Report Tool
        </CardHeader>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <div className="date-section">
              <div className="date-row">
                <Form.Group className="date-group">
                  <Form.Label className="date-label">
                    <i className="fas fa-calendar-alt calendar-icon"></i>
                    Start Date
                  </Form.Label>
                  <DateInput.New
                    value={[form.startDate]}
                    onChange={(start) => {
                      const today = new Dayts().startOf('day');
                      const val = new Dayts(new Dayts(start).startOf('day'));
                      const end = form.endDate ? new Dayts(form.endDate).endOf('day') : false;
                      if (val.isSameOrBefore(today))
                        return createNotification('Date must be in the future.', Toast.Type.WARNING, 'Invalid Date');
                      handleInputChange('startDate', val.formatAs());
                      if (!end || val.isAfter(end)) handleInputChange('endDate', new Dayts(new Dayts(val).endOf('day')).formatAs());
                      endDateRef.current?.click();
                    }}
                    placeholder={Dayts.Format.DATE_FE_FULL}
                    format={Dayts.Format.DATE_FE_FULL}
                    className="date-input"
                    isInvalid={!isValidDateRange()}
                    ref={startDateRef}
                  />
                </Form.Group>
                <Form.Group className="date-group">
                  <Form.Label className="date-label">
                    <i className="fas fa-calendar-alt calendar-icon"></i>
                    End Date
                  </Form.Label>
                  <DateInput.New
                    value={[form.endDate]}
                    onChange={(end) => {
                      const today = new Dayts().startOf('day');
                      const val = new Dayts(new Dayts(end).endOf('day'));
                      const start = form.startDate ? new Dayts(form.startDate).startOf('day') : false;
                      if (val.isSameOrBefore(today))
                        return createNotification('Date must be in the future.', Toast.Type.WARNING, 'Invalid Date');
                      handleInputChange('endDate', val.formatAs());
                      if (!start || val.isBefore(start))
                        handleInputChange('startDate', new Dayts(new Dayts(val).startOf('day')).formatAs());
                      endDateRef.current?.click();
                    }}
                    placeholder={Dayts.Format.DATE_FE_FULL}
                    format={Dayts.Format.DATE_FE_FULL}
                    className="date-input"
                    isInvalid={!isValidDateRange()}
                    ref={endDateRef}
                  />
                </Form.Group>
              </div>
            </div>
            <div className="locations-section">
              <Form.Group className="location-group">
                <Form.Label className="location-label">Pickup Location</Form.Label>
                <LocationInput
                  name="puAddress"
                  placeholder="Enter pickup location..."
                  onChange={(val) => handleInputChange('puAddress', val)}
                  onBlur={(e) => {
                    handleInputChange('puAddress', e.target.value);
                    handleRouteInputChange('puAddress');
                  }}
                  onKeyDown={onEnter((e: any) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRouteInputChange('puAddress');
                  })}
                  defaultValue={form.puAddress}
                  ref={pickupInputRef}
                />
              </Form.Group>
              <Form.Group className="location-group">
                <Form.Label className="location-label">Dropoff Location</Form.Label>
                <LocationInput
                  name="doAddress"
                  placeholder="Enter dropoff location..."
                  onChange={(val) => handleInputChange('doAddress', val)}
                  onBlur={(e) => {
                    handleInputChange('doAddress', e.target.value);
                    handleRouteInputChange('doAddress');
                  }}
                  onKeyDown={onEnter((e: any) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRouteInputChange('doAddress');
                  })}
                  defaultValue={form.doAddress}
                  ref={dropoffInputRef}
                />
              </Form.Group>
            </div>
            <div className="submit-section">
              <Button variant="primary" type="submit" className="submit-btn" onClick={handleSubmit} disabled={!isFormValid() || loading}>
                {loading && <i className="fas fa-spinner fa-pulse me-1" />}
                {!loading && <i className="fas fa-search search-icon-btn" />}
                Create Drive Time
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Controls;
